import type { BuyingOrderRow, ListBuyingOrdersInput } from '@erp/rpc';

export type BuyingOrderSort = NonNullable<ListBuyingOrdersInput['sort']>;
export type BuyingOrderLineType = NonNullable<
  ListBuyingOrdersInput['lineTypes']
>[number];
export type BuyingGroupBy = NonNullable<ListBuyingOrdersInput['groupBy']>;
export type SortDirection = 'asc' | 'desc';

export const BUYING_ORDERS_PAGE_SIZE = 100;

export const ALL_LINE_TYPES: readonly BuyingOrderLineType[] = [
  'back_order',
  'potf',
  'stock',
];

const SORT_KEYS: readonly BuyingOrderSort[] = [
  'priority',
  'value',
  'oldest_so',
  'name',
  'lines',
  'back_order_value',
  'potf_value',
  'stock_value',
  'shareable_value',
];

const DESCENDING_SORTS: readonly BuyingOrderSort[] = [
  'value',
  'lines',
  'back_order_value',
  'potf_value',
  'stock_value',
  'shareable_value',
];

/** Server default direction per sort key (mirrors the contract comment). */
export function defaultSortDirection(sort: BuyingOrderSort): SortDirection {
  return DESCENDING_SORTS.includes(sort) ? 'desc' : 'asc';
}

/**
 * `?sort` used to carry the supplier belt's comma-separated sort stack
 * ("potf,so_value"). Anything that is not a single known key falls back to the
 * default ranking rather than breaking the page.
 */
export function parseBuyingSort(
  sort: string | undefined,
  sortDir: string | undefined,
): { sort: BuyingOrderSort; direction: SortDirection } {
  const key = SORT_KEYS.find((k) => k === sort) ?? 'priority';
  const direction =
    sortDir === 'asc' || sortDir === 'desc'
      ? sortDir
      : defaultSortDirection(key);
  return { sort: key, direction };
}

/** Header click: a new column starts at its default direction, the same column flips. */
export function nextBuyingSort(
  current: { sort: BuyingOrderSort; direction: SortDirection },
  clicked: BuyingOrderSort,
): { sort: BuyingOrderSort; direction: SortDirection } {
  if (current.sort === clicked) {
    return {
      sort: clicked,
      direction: current.direction === 'asc' ? 'desc' : 'asc',
    };
  }
  return { sort: clicked, direction: defaultSortDirection(clicked) };
}

/** URL form of a sort: the default ranking is stored as absent params. */
export function serializeBuyingSort(next: {
  sort: BuyingOrderSort;
  direction: SortDirection;
}): { sort: string | undefined; sortDir: SortDirection | undefined } {
  if (next.sort === 'priority') return { sort: undefined, sortDir: undefined };
  return { sort: next.sort, sortDir: next.direction };
}

/** Comma-separated `?lineTypes`. Empty or unknown input means "all types". */
export function parseLineTypes(
  value: string | undefined,
): BuyingOrderLineType[] {
  if (!value) return [...ALL_LINE_TYPES];
  const wanted = new Set(value.split(','));
  const picked = ALL_LINE_TYPES.filter((t) => wanted.has(t));
  return picked.length > 0 ? picked : [...ALL_LINE_TYPES];
}

/** All three (the default) is stored as an absent param. */
export function serializeLineTypes(
  types: readonly BuyingOrderLineType[],
): string | undefined {
  const picked = ALL_LINE_TYPES.filter((t) => types.includes(t));
  if (picked.length === 0 || picked.length === ALL_LINE_TYPES.length) {
    return undefined;
  }
  return picked.join(',');
}

/** 1-based `?page` to a row offset; junk input is page 1. */
export function pageToOffset(page: number | undefined): number {
  if (page === undefined || !Number.isInteger(page) || page < 1) return 0;
  return (page - 1) * BUYING_ORDERS_PAGE_SIZE;
}

/** `?groupBy`; vendor_location (a row per cart) is the default and stays absent. */
export function parseGroupBy(value: string | undefined): BuyingGroupBy {
  return value === 'vendor' ? 'vendor' : 'vendor_location';
}

export function serializeGroupBy(groupBy: BuyingGroupBy): string | undefined {
  return groupBy === 'vendor' ? 'vendor' : undefined;
}

// ── Row selection <-> existing detail params ───────────────────────────────

export interface BuyingSelectionParams {
  supplierId?: string;
  /** A transfer row's source location (the transfer board, GEN-3026). */
  sourceId?: string;
  shipTo?: string;
}

/**
 * A cart row opens its owner's board scoped to the cart's ship-to: a PO row
 * the supplier's (`supplierId`), a TO row the source location's (`sourceId`).
 * A vendor row has no ship-to, so it opens the owner's whole board (every
 * cart, with its cart chip rail). Every key is set, so a pick clears the other.
 */
export function selectionForRow(row: BuyingOrderRow): BuyingSelectionParams {
  return {
    supplierId: row.kind === 'po' ? (row.supplierId ?? undefined) : undefined,
    sourceId:
      row.kind === 'to' ? (row.sourceWarehouseId ?? undefined) : undefined,
    shipTo: row.shipToWarehouseId ?? undefined,
  };
}

/** A selection without `shipTo` marks every cart of that owner. */
export function isRowSelected(
  row: BuyingOrderRow,
  selection: BuyingSelectionParams,
): boolean {
  const [selected, owner] =
    row.kind === 'to'
      ? [selection.sourceId, row.sourceWarehouseId]
      : [selection.supplierId, row.supplierId];
  if (selected == null || selected !== owner) return false;
  return selection.shipTo == null || selection.shipTo === row.shipToWarehouseId;
}

export function buyingRowKey(row: BuyingOrderRow): string {
  return `${row.kind}:${row.supplierId ?? row.sourceWarehouseId ?? ''}:${row.shipToWarehouseId ?? ''}`;
}

export function toBuyingLineTypes(
  ids: readonly string[],
): BuyingOrderLineType[] {
  return ALL_LINE_TYPES.filter((t) => ids.includes(t));
}
